import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { parseRecommendationBatchAggregate } from '@apps/web/components/phoebe_chat/recommendation_aggregate';
import { PhoebeAgentMark } from '@apps/web/components/phoebe_chat_view/brand_marks';
import { RecommendationDecisionCarousel } from '@apps/web/components/phoebe_chat_view/tool_calls/recommendation_decision_carousel';
import {
    type ActivityEntry,
    type CompactionMarkerEntry,
    type DebugEventEntry,
    type DebugEventGroupEntry,
    type SubagentResultEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { Badge } from '@apps/web/components/ui/badge';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';

export { parseRecommendationBatchAggregate } from '@apps/web/components/phoebe_chat/recommendation_aggregate';

function getDebugEventBadgeLabel(entry: DebugEventEntry): string {
    if (entry.eventOffset !== null) {
        return `#${entry.eventOffset}`;
    }
    if (entry.source === 'pending' && entry.status === 'pending') {
        return 'queued';
    }
    return entry.status ?? entry.source;
}

function shortDebugEventId(value: string): string {
    return value.length > 8 ? value.slice(0, 8) : value;
}

function getDebugEventDisplayId(entry: DebugEventEntry): string {
    const rawId = entry.raw['id'];
    return shortDebugEventId(typeof rawId === 'string' ? rawId : entry.id);
}

export function DebugEventMarker({
    entry,
    nested = false,
}: {
    readonly entry: DebugEventEntry;
    readonly nested?: boolean;
}) {
    const { timezone } = usePhoebeChatOrganizationScope();
    const [isExpanded, setIsExpanded] = useState(false);
    const rawJson = useMemo(
        () => JSON.stringify(entry.raw, null, 2),
        [entry.raw],
    );
    const statusLabel =
        entry.source === 'pending' && entry.status !== 'pending'
            ? entry.status
            : null;

    return (
        <div className={nested ? '' : 'flex justify-center'}>
            <div
                className={cn(
                    'border-border/70 bg-muted/20 w-full rounded-md border border-dashed',
                    nested ? '' : 'max-w-2xl',
                )}
            >
                <button
                    type="button"
                    className="flex w-full items-start gap-2 px-3 py-2 text-left"
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Hide' : 'Show'} ${
                        entry.eventType
                    } event payload`}
                    onClick={() => setIsExpanded((current) => !current)}
                >
                    {isExpanded ? (
                        <ChevronDown className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
                    ) : (
                        <ChevronRight className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
                    )}
                    <Badge
                        variant={
                            entry.source === 'event' ? 'secondary' : 'outline'
                        }
                        className="mt-0.5 h-5 rounded-sm px-1.5 font-mono text-[10px]"
                    >
                        {getDebugEventBadgeLabel(entry)}
                    </Badge>
                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-mono text-xs">
                                {entry.eventType}
                            </span>
                            {entry.toolName ? (
                                <span className="font-mono text-xs font-semibold">
                                    {entry.toolName}
                                </span>
                            ) : null}
                            {statusLabel ? (
                                <span className="text-muted-foreground font-mono text-[10px]">
                                    {statusLabel}
                                </span>
                            ) : null}
                        </div>
                        <div className="text-muted-foreground mt-0.5 font-mono text-[10px]">
                            {formatDateAndTime(entry.createdAt, timezone)} |{' '}
                            {getDebugEventDisplayId(entry)}
                        </div>
                    </div>
                </button>
                {isExpanded ? (
                    <pre className="bg-background/70 mx-3 mb-3 max-h-72 overflow-auto rounded-sm border p-2 text-[10px] leading-relaxed whitespace-pre-wrap">
                        {rawJson}
                    </pre>
                ) : null}
            </div>
        </div>
    );
}

function getDebugEventGroupCountLabel(count: number): string {
    return `${count} event${count === 1 ? '' : 's'}`;
}

function getDebugEventGroupTimeRange(
    events: ReadonlyArray<DebugEventEntry>,
    timezone: string,
): string {
    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];
    if (!firstEvent || !lastEvent) {
        return '';
    }

    const firstTime = formatDateAndTime(firstEvent.createdAt, timezone);
    const lastTime = formatDateAndTime(lastEvent.createdAt, timezone);
    return firstTime === lastTime ? firstTime : `${firstTime} - ${lastTime}`;
}

export function DebugEventGroupMarker({
    entry,
}: {
    readonly entry: DebugEventGroupEntry;
}) {
    const { timezone } = usePhoebeChatOrganizationScope();
    const [isExpanded, setIsExpanded] = useState(false);
    const countLabel = getDebugEventGroupCountLabel(entry.events.length);
    const timeRange = getDebugEventGroupTimeRange(entry.events, timezone);

    return (
        <div className="flex justify-start">
            <div className="text-muted-foreground w-full max-w-full text-left text-xs break-words">
                <button
                    type="button"
                    className="text-muted-foreground/70 hover:text-foreground flex cursor-pointer items-center gap-2 py-2 text-left transition-colors"
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Hide' : 'Show'} ${countLabel}`}
                    onClick={() => setIsExpanded((current) => !current)}
                >
                    <span>{countLabel}</span>
                    {timeRange ? (
                        <span className="text-foreground/70 max-w-[20rem] truncate font-medium">
                            {timeRange}
                        </span>
                    ) : null}
                    {isExpanded ? (
                        <ChevronDown className="text-muted-foreground/70 h-3 w-3" />
                    ) : (
                        <ChevronRight className="text-muted-foreground/70 h-3 w-3" />
                    )}
                </button>
                {isExpanded ? (
                    <div className="mt-1 space-y-2">
                        {entry.events.map((event) => (
                            <DebugEventMarker
                                key={event.id}
                                entry={event}
                                nested={true}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function formatSubagentStatusLabel(status: string): string {
    const normalizedStatus = status.trim().toLowerCase();
    if (normalizedStatus === 'completed') {
        return 'Subagent completed';
    }
    if (normalizedStatus === 'failed') {
        return 'Subagent failed';
    }
    if (normalizedStatus === 'interrupted') {
        return 'Subagent interrupted';
    }
    if (normalizedStatus === 'cancelled') {
        return 'Subagent cancelled';
    }
    return `Subagent ${status}`;
}

function truncateSubagentMarkerText(text: string): string {
    const normalizedText = text.trim().replace(/\s+/g, ' ');
    if (normalizedText.length <= 280) {
        return normalizedText;
    }
    return `${normalizedText.slice(0, 277)}…`;
}

export function SubagentResultMarker({
    canOpenSubagentRun,
    entry,
    recommendationOrganizationId,
    offerStartOutreach = true,
    groupPromptSlot = true,
    onOpenSubagentRun,
    showRecommendationTraceProgress = false,
}: {
    readonly canOpenSubagentRun?: (childRunId: string) => boolean;
    readonly entry: SubagentResultEntry;
    readonly recommendationOrganizationId?: string | null;
    readonly offerStartOutreach?: boolean;
    readonly groupPromptSlot?: boolean;
    readonly onOpenSubagentRun?: (childRunId: string) => void;
    readonly showRecommendationTraceProgress?: boolean;
}) {
    const { timezone } = usePhoebeChatOrganizationScope();
    const isError =
        entry.terminalStatus === 'failed' ||
        entry.terminalStatus === 'interrupted' ||
        entry.terminalStatus === 'cancelled';
    const detail = entry.errorMessage ?? entry.finalAssistantText;
    // Memoized so the carousel's `aggregateDecisions` prop keeps a stable
    // identity across re-renders: a fresh parse per render would re-derive
    // the carousel's whole memo chain and re-fire its fan-out report effect
    // on every render.
    const batchAggregate = useMemo(
        () => parseRecommendationBatchAggregate(entry.finalAssistantText),
        [entry.finalAssistantText],
    );
    if (batchAggregate) {
        return (
            <div className="relative ml-7 min-w-0">
                <PhoebeAgentMark className="absolute top-2 -left-7 size-5" />
                <RecommendationDecisionCarousel
                    batchId={batchAggregate.batchId}
                    candidateCount={batchAggregate.decisionCount}
                    aggregateDecisions={batchAggregate.decisions}
                    organizationIdOverride={recommendationOrganizationId}
                    offerStartOutreach={offerStartOutreach}
                    groupPromptSlot={groupPromptSlot}
                    showTraceProgress={showRecommendationTraceProgress}
                />
            </div>
        );
    }
    const childRunId = entry.childRunId;
    const handleOpen =
        onOpenSubagentRun &&
        childRunId &&
        (canOpenSubagentRun?.(childRunId) ?? true)
            ? () => onOpenSubagentRun(childRunId)
            : null;
    const canOpen = handleOpen !== null;
    const cardClassName = cn(
        'border-border bg-background inline-flex max-w-full flex-wrap items-center gap-2 rounded-[6px] border px-3 py-2 text-left text-xs shadow-[0_1px_0_rgba(15,23,42,0.03)]',
        canOpen &&
            'hover:border-border hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    );
    const content = (
        <Fragment>
            <span className="text-foreground/80 font-medium">
                Child session
            </span>
            <Badge
                variant={isError ? 'destructive' : 'secondary'}
                className="rounded-full px-2 py-0 text-[11px]"
            >
                {formatSubagentStatusLabel(entry.terminalStatus)}
            </Badge>
            <span className="text-muted-foreground/70">
                {formatDateAndTime(entry.createdAt, timezone)}
            </span>
            {childRunId ? (
                <span className="text-muted-foreground/70 font-mono text-[11px]">
                    {childRunId.slice(0, 8)}
                </span>
            ) : null}
            {detail ? (
                <span className="text-foreground/80 min-w-0 break-words">
                    {truncateSubagentMarkerText(detail)}
                </span>
            ) : null}
        </Fragment>
    );

    return (
        <div className="relative ml-7 flex justify-start">
            <PhoebeAgentMark className="absolute top-2 -left-7 size-5" />
            {handleOpen ? (
                <button
                    type="button"
                    className={cardClassName}
                    aria-label={`Open child session ${childRunId}`}
                    onClick={handleOpen}
                >
                    {content}
                </button>
            ) : (
                <div className={cardClassName}>{content}</div>
            )}
        </div>
    );
}

export function CompactionMarker({
    entry,
}: {
    readonly entry: CompactionMarkerEntry;
}) {
    const detail =
        entry.replacedItemCount === null
            ? 'History compacted'
            : `History compacted (${entry.replacedItemCount} earlier message${
                  entry.replacedItemCount === 1 ? '' : 's'
              } condensed)`;

    return (
        <div className="flex justify-center">
            <div className="border-border/70 bg-muted/40 max-w-3xl rounded-md border px-3 py-2 text-xs">
                <div className="text-muted-foreground text-center italic">
                    {detail}
                </div>
                {entry.summaryText ? (
                    <details className="mt-2 text-left">
                        <summary className="text-muted-foreground cursor-pointer font-medium">
                            Summary
                        </summary>
                        <div className="bg-background mt-2 max-h-80 overflow-auto rounded border px-3 py-2 whitespace-pre-wrap">
                            {entry.summaryText}
                        </div>
                    </details>
                ) : null}
            </div>
        </div>
    );
}

export function LifecycleChip({
    entry,
    onRetryAfterRunError,
    isRetryDisabled = false,
}: {
    entry: ActivityEntry;
    onRetryAfterRunError?: () => void;
    isRetryDisabled?: boolean;
}) {
    const { timezone } = usePhoebeChatOrganizationScope();
    const badgeVariant =
        entry.tone === 'success'
            ? 'success'
            : entry.tone === 'destructive'
              ? 'destructive'
              : 'outline';

    return (
        <div className="flex justify-start">
            <div className="border-border/50 bg-muted/40 inline-flex max-w-full flex-wrap items-center gap-2 rounded-lg border px-3 py-1.5 text-xs">
                <Badge variant={badgeVariant} className="rounded-md px-2 py-0">
                    {entry.label}
                </Badge>
                <span className="text-muted-foreground">
                    {formatDateAndTime(entry.createdAt, timezone)}
                </span>
                {entry.detail ? (
                    <span className="text-foreground break-words">
                        {entry.detail}
                    </span>
                ) : null}
                {entry.canRetryRunError && onRetryAfterRunError ? (
                    <button
                        type="button"
                        disabled={isRetryDisabled}
                        onClick={onRetryAfterRunError}
                        className="text-destructive cursor-pointer rounded-md border border-red-200 bg-red-50 px-2 py-0.5 font-medium hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900 dark:bg-red-950/50 dark:hover:bg-red-950"
                    >
                        Click here to retry
                    </button>
                ) : null}
            </div>
        </div>
    );
}
